import dossierApi from '@/api/_constantes-dossier'
import infirmierApi from '@/api/_infirmier'
import {
    ECHELLES, COULEUR_REPERE_37, INTERVALLES, SOUS_LIGNES, HEURES_REPERES, echelle, graduations, versY, jourUn, nombreJours, versX, segments, taEnCmHg, libelleCmHg
} from '@/utils/clinical/surveillance-constantes'

const COLONNE = 58, JOUR = 96, ENTETE = 48, MARGE = 18, HAUTEUR = 400
// Scale columns, left to right; the first one (Diurèse) is wider for its title.
const LARGEURS = ECHELLES.map(e => e.largeur || COLONNE)
const DEBUTS = LARGEURS.map((_, i) => LARGEURS.slice(0, i).reduce((a, b) => a + b, 0))
const GAUCHE = LARGEURS.reduce((a, b) => a + b, 0)
const COURBES = ['diurese', 'temperature', 'pouls', 'fr']
const couleurValide = c => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)
const formatDate = date => new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
const formatDateHeure = date => new Date(date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
const nombre = n => String(n).replace('.', ',')

export default {
    name: 'feuille-surveillance-constantes',
    props: {
        consultationId: { type: Number, default: null },
        visible: Boolean,
        // 'infirmier' or 'medecin'; defaults to the active role.
        acces: { type: String, default: null }
    },
    data() { return { loading: false, erreur: '', feuille: null, sequence: 0, gauche: GAUCHE, jour: JOUR, entete: ENTETE, marge: MARGE, hauteur: HAUTEUR } },
    watch: {
        visible(open) { if (open) this.charger() },
        consultationId(id, old) { if (this.visible && id !== old) this.charger() }
    },
    mounted() { if (this.visible) this.charger() },
    beforeDestroy() { this.sequence++ },
    computed: {
        modeAcces() {
            if (this.acces) return this.acces
            const role = this.$store && this.$store.getters && this.$store.getters['activeRole']
            return role && role.nom === 'ROLE_INFIRMIER' ? 'infirmier' : role && role.nom === 'ROLE_SAGE_FEMME' ? 'sage-femme' : 'medecin'
        },
        motifId() { return 'fsc-hachures-' + this._uid },
        couleurs() { return this.feuille && this.feuille.couleurs || {} },
        echelles() { return ECHELLES.map(e => Object.assign({}, e, couleurValide(this.couleurs[e.key]) ? { couleur: this.couleurs[e.key] } : {})) },
        couleur37() { return couleurValide(this.couleurs.repere_37) ? this.couleurs.repere_37 : COULEUR_REPERE_37 },
        points() { return this.feuille && Array.isArray(this.feuille.points) ? this.feuille.points : [] },
        j1() { return this.feuille ? jourUn(this.feuille.debut, this.points) : null },
        jours() { return nombreJours(this.j1, this.points) },
        largeur() { return GAUCHE + this.jours * JOUR + 12 },
        hauteurTotale() { return ENTETE + MARGE + HAUTEUR + MARGE + 6 },
        // Readable up to J11 at modal width; longer stays scroll only on screen.
        largeurMin() { return this.jours > 11 ? this.largeur + 'px' : null },
        lignes() {
            const n = INTERVALLES * SOUS_LIGNES
            return Array.from({ length: n + 1 }, (_, i) => ({ y: this.haut + i * HAUTEUR / n, majeure: i % SOUS_LIGNES === 0 }))
        },
        verticales() {
            const result = []
            for (let d = 0; d <= this.jours; d++) {
                for (let q = 0; q < 8 && (d < this.jours || q === 0); q++) {
                    result.push({ x: GAUCHE + d * JOUR + q * JOUR / 8, niveau: q === 0 ? 'jour' : q % 2 === 0 ? 'repere' : 'fine' })
                }
            }
            return result
        },
        enTetesJours() {
            return Array.from({ length: this.jours }, (_, i) => {
                const date = this.j1 ? new Date(this.j1.getTime()) : null
                if (date) date.setDate(date.getDate() + i)
                return { x: GAUCHE + i * JOUR, libelle: 'J' + (i + 1), date: date ? formatDate(date) : '' }
            })
        },
        heures() {
            const result = []
            for (let i = 0; i < this.jours; i++) HEURES_REPERES.forEach(h => result.push({ x: GAUCHE + i * JOUR + h / 24 * JOUR, libelle: String(h).padStart(2, '0') }))
            return result
        },
        graduationsEchelles() {
            return this.echelles.map((e, i) => ({
                e, x0: DEBUTS[i], w: LARGEURS[i], x: DEBUTS[i] + LARGEURS[i] / 2,
                valeurs: graduations(e).slice(0, INTERVALLES).map((v, j) => ({ libelle: e.key === 'temperature' ? v + '°' : String(v), y: this.haut + j * HAUTEUR / INTERVALLES }))
            }))
        },
        haut() { return ENTETE + MARGE },
        ligne37() { return this.haut + versY(37, echelle('temperature'), HAUTEUR).y },
        courbes() {
            if (!this.j1) return []
            return COURBES.map(key => {
                const e = this.echelle(key)
                const traces = segments(this.points, key).map(segment => segment.map(point => this.position(point, point[key], e)))
                return { key, couleur: e.couleur, traces, polylignes: traces.map(t => t.map(p => p.x + ',' + p.y).join(' ')) }
            })
        },
        barresTa() {
            if (!this.j1) return []
            const e = this.echelle('ta')
            return this.points.filter(point => point.ta).map(point => {
                const ta = taEnCmHg(point.ta), haut = this.position(point, ta.sys, e), bas = this.position(point, ta.dia, e)
                return { x: haut.x, ySys: haut.y, yDia: bas.y, hors: haut.hors || bas.hors, sys: libelleCmHg(ta.sys), dia: libelleCmHg(ta.dia), date: point.date }
            })
        },
        colonnesSurvol() {
            if (!this.j1) return []
            return this.points.map(point => ({ x: this.position(point, 0, this.echelle('temperature')).x, titre: this.resume(point), date: point.date }))
        },
        description() {
            const n = this.points.length
            return 'Feuille de surveillance des constantes : ' + n + ' prise' + (n > 1 ? 's' : '') + ' de ' + (this.enTetesJours[0] ? this.enTetesJours[0].date : '') + '.'
        }
    },
    methods: {
        echelle(key) { return this.echelles.find(e => e.key === key) || echelle(key) },
        position(point, valeur, e) {
            const { y, hors } = versY(valeur, e, HAUTEUR)
            return { x: GAUCHE + versX(point.date, this.j1, JOUR), y: this.haut + y, hors, point }
        },
        formatDateHeure,
        valeur(point, key) {
            if (key === 'ta') { const ta = point.ta; return ta ? nombre(ta.sys_mmhg / 10) + '/' + nombre(ta.dia_mmhg / 10) + ' cmHg' : '—' }
            const v = point[key]
            return typeof v === 'number' ? nombre(v) + ' ' + echelle(key).unite : '—'
        },
        resume(point) {
            const lignes = [formatDateHeure(point.date), 'Diurèse : ' + this.valeur(point, 'diurese'), 'T° : ' + this.valeur(point, 'temperature'), 'Pouls : ' + this.valeur(point, 'pouls'),
                'F.R : ' + this.valeur(point, 'fr'), 'T.A : ' + this.valeur(point, 'ta')]
            if (point.ta && point.ta.brut) Object.keys(point.ta.brut).forEach(bras => lignes.push('  bras ' + bras + ' : ' + point.ta.brut[bras]))
            if (point.saisi_par) lignes.push('Saisie : ' + point.saisi_par)
            return lignes.join('\n')
        },
        async charger() {
            const sequence = ++this.sequence, id = this.consultationId, acces = this.modeAcces
            this.feuille = null; this.erreur = ''
            if (!id) { this.erreur = 'Consultation introuvable.'; return }
            this.loading = true
            try {
                const service = acces === 'sage-femme' ? dossierApi.sageFemme : dossierApi
                const credential = await (acces === 'infirmier' ? infirmierApi.getTableauInfirmierAccess() : service.getConstantesAccess())
                if (sequence !== this.sequence) return
                if (!credential || !credential.success || !credential.data || !credential.data.access_token) throw new Error('Accès indisponible')
                const response = await service.lireSurveillance({ consultation_id: id, access_token: credential.data.access_token, acces })
                if (sequence !== this.sequence) return
                if (!response || !Array.isArray(response.points)) throw new Error('Réponse inattendue')
                this.feuille = response
            } catch (error) {
                if (sequence !== this.sequence) return
                const status = error && error.response && error.response.status
                this.erreur = status === 404 ? 'Consultation indisponible pour votre accès.'
                    : status === 401 || status === 403 ? 'Accès aux constantes refusé pour cette consultation.'
                    : 'Impossible de charger la feuille de surveillance. Réessayez.'
            } finally { if (sequence === this.sequence) this.loading = false }
        },
        imprimer() {
            if (typeof window === 'undefined' || !this.feuille) return
            // @page is global: add it only for this print, then remove it.
            const style = document.createElement('style')
            style.textContent = '@page { size: A4 landscape; margin: 8mm; }'
            document.head.appendChild(style)
            document.body.classList.add('fsc-impression')
            const nettoyer = () => { document.body.classList.remove('fsc-impression'); style.remove(); window.removeEventListener('afterprint', nettoyer) }
            window.addEventListener('afterprint', nettoyer)
            window.print()
            setTimeout(nettoyer, 1000)
        }
    }
}
